/**
 * VisitsWidget.jsx
 * =================
 * Widget Cockpit Dirigeant — Synthèse des visites.
 * Affiche : nombre de visites ce mois, coverage par mandat, visites sans retour, délai moyen 1ère visite.
 */
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import api from '../../lib/api';
import {
    Eye, Clock, AlertCircle, TrendingUp, ArrowRight, Loader2,
} from 'lucide-react';

export default function VisitsWidget() {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({});

    useEffect(() => {
        (async () => {
            try {
                const { data: res } = await api.get('/kpi/compute', {
                    params: { ids: 'visits_count,time_to_first_visit,post_visit_return_rate,visits_per_mandate,visits_no_return' }
                });
                setData(res?.results || {});
            } catch (e) {
                // silent
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (loading) {
        return (
            <div className="rounded-2xl p-4 bg-card border border-white/10 space-y-2" data-testid="visits-widget-loading">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-white/5 animate-pulse" />
                    <div className="h-3 w-32 rounded bg-white/5 animate-pulse" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                    {[0,1,2,3].map(i => <div key={i} className="h-14 rounded-lg bg-white/[0.03] animate-pulse" />)}
                </div>
            </div>
        );
    }

    const visitsCount = data.visits_count?.value || 0;
    const thisMonth = data.visits_count?.meta?.this_month || 0;
    const byStatus = data.visits_count?.meta?.by_status || {};
    const timeToFirst = data.time_to_first_visit?.value;
    const returnRate = data.post_visit_return_rate?.value;
    const coverage = data.visits_per_mandate?.meta?.coverage_pct;
    const avgPerMandate = data.visits_per_mandate?.value;
    const noReturn = data.visits_no_return?.value || 0;
    const noReturnVisits = data.visits_no_return?.meta?.visits || [];

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative rounded-2xl overflow-hidden border border-[#681793]/20 bg-gradient-to-br from-[#681793]/10 via-card to-[#681793]/5"
            data-testid="visits-widget"
        >
            <div className="absolute top-0 right-0 w-40 h-40 bg-[#681793]/10 rounded-full blur-[70px] pointer-events-none" />

            <div className="relative p-4 sm:p-5">
                <div className="flex items-center justify-between mb-4 gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#681793] to-[#9b4dca] flex items-center justify-center shrink-0">
                            <Eye className="w-5 h-5 text-white" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-[10px] text-[#9b4dca] font-bold uppercase tracking-wider">Activité visites</p>
                            <p className="text-base font-bold text-foreground">{thisMonth} visite{thisMonth > 1 ? 's' : ''} ce mois · {visitsCount} au total</p>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                    <StatTile
                        icon={Eye}
                        label="Visites / mandat"
                        value={avgPerMandate !== null && avgPerMandate !== undefined ? avgPerMandate : '—'}
                        sub={coverage !== undefined ? `${coverage}% couverts` : ''}
                    />
                    <StatTile
                        icon={Clock}
                        label="Délai 1ère visite"
                        value={timeToFirst !== null && timeToFirst !== undefined ? `${timeToFirst} j` : '—'}
                        sub={data.time_to_first_visit?.meta?.count ? `${data.time_to_first_visit.meta.count} mandats` : ''}
                    />
                    <StatTile
                        icon={TrendingUp}
                        label="Retours post-visite"
                        value={returnRate !== null && returnRate !== undefined ? `${returnRate}%` : '—'}
                        sub={data.post_visit_return_rate?.meta?.completed ? `${data.post_visit_return_rate.meta.with_report}/${data.post_visit_return_rate.meta.completed}` : ''}
                        color={returnRate >= 80 ? 'green' : returnRate >= 50 ? 'amber' : 'red'}
                    />
                    <StatTile
                        icon={AlertCircle}
                        label="À relancer"
                        value={noReturn}
                        sub="opportunités"
                        color={noReturn === 0 ? 'green' : noReturn <= 5 ? 'amber' : 'red'}
                    />
                </div>

                {/* Liste visites sans retour (actionnable) */}
                {noReturnVisits.length > 0 && (
                    <div className={`rounded-xl p-3 border ${
                        noReturnVisits.length <= 5
                            ? 'bg-amber-500/5 border-amber-500/20'
                            : 'bg-red-500/5 border-red-500/20'
                    }`}>
                        <p className={`text-[10px] uppercase tracking-wider font-bold mb-2 flex items-center gap-1.5 ${
                            noReturnVisits.length <= 5 ? 'text-amber-300' : 'text-red-300'
                        }`}>
                            <AlertCircle className="w-3 h-3" />
                            Visites à relancer {noReturnVisits.length <= 5 ? '(opportunités)' : ''}
                        </p>
                        <div className="space-y-1">
                            {noReturnVisits.slice(0, 3).map((v, i) => (
                                <Link
                                    key={v.id || i}
                                    to={v.mandate_id ? `/admin/mandates/${v.mandate_id}` : '#'}
                                    className="flex items-center justify-between px-2 py-1.5 rounded hover:bg-white/5 text-xs group"
                                    data-testid={`visit-no-return-${i}`}
                                >
                                    <div className="min-w-0 flex-1">
                                        <p className="text-foreground truncate">{v.visitor_name || 'Acquéreur'}</p>
                                        <p className="text-[10px] text-muted-foreground">{v.scheduled_date || '—'}</p>
                                    </div>
                                    <ArrowRight className={`w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity ${
                                        noReturnVisits.length <= 5 ? 'text-amber-300' : 'text-red-300'
                                    }`} />
                                </Link>
                            ))}
                        </div>
                        {noReturnVisits.length > 3 && (
                            <p className="text-[10px] text-red-300/70 text-center pt-1">+ {noReturnVisits.length - 3} autre{noReturnVisits.length - 3 > 1 ? 's' : ''}</p>
                        )}
                    </div>
                )}

                {/* Breakdown statuts */}
                {Object.keys(byStatus).length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap mt-3 pt-3 border-t border-white/5">
                        <span className="text-[9px] text-muted-foreground">Statuts :</span>
                        {Object.entries(byStatus).map(([status, count]) => (
                            <span
                                key={status}
                                className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${
                                    status === 'completed' ? 'bg-green-500/10 text-green-300' :
                                    status === 'scheduled' ? 'bg-blue-500/10 text-blue-300' :
                                    status === 'cancelled' ? 'bg-red-500/10 text-red-300' :
                                    status === 'no_show' ? 'bg-amber-500/10 text-amber-300' :
                                    'bg-white/5 text-muted-foreground'
                                }`}
                            >
                                {status}: {count}
                            </span>
                        ))}
                    </div>
                )}
            </div>
        </motion.div>
    );
}

function StatTile({ icon: Icon, label, value, sub, color = 'default' }) {
    const colorMap = {
        green: 'text-green-400',
        amber: 'text-amber-400',
        red: 'text-red-400',
        muted: 'text-muted-foreground',
        default: 'text-foreground',
    };
    return (
        <div className="p-2.5 rounded-lg bg-white/[0.03] border border-white/10">
            <div className="flex items-center gap-1.5 mb-1">
                <Icon className={`w-3 h-3 ${colorMap[color] || colorMap.default}`} />
                <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold truncate">{label}</p>
            </div>
            <p className={`text-lg font-black leading-none ${colorMap[color] || colorMap.default}`}>{value}</p>
            {sub && <p className="text-[9px] text-muted-foreground mt-1 truncate">{sub}</p>}
        </div>
    );
}
